/**
 * DataTableStoreSync - Syncs React Table state to/from BYOS adapter
 *
 * On initial mount: seeds the table's columnFilters from BYOS state (BYOS → Table)
 * and establishes the applied-filter baseline. This handles URL params available in
 * BYOS that useState missed during SSR/hydration (e.g. nuqs reads URL after render).
 *
 * Filters apply on demand, not on keystroke. Filter components update the table
 * directly via column.setFilterValue() — that is the live *draft*. The draft is
 * only pushed to the BYOS adapter (URL / query) when applyFilters() is called,
 * e.g. from the Search button in the filter panel. This keeps an expensive query
 * from firing on every partial edit. Sorting and row selection still sync
 * immediately (they are not part of the filter panel).
 */
import { useStoreContext } from '@/lib/store/context'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { isStateEqual } from '@/lib/store/schema'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDataTable } from './data-table-provider'

interface FilterApplyContextValue {
  /** Push the current draft filters to the URL / query. */
  applyFilters: () => void
  /** True when the draft differs from the currently applied filters. */
  isDirty: boolean
}

const FilterApplyContext = createContext<FilterApplyContextValue | null>(null)

export function useFilterApply(): FilterApplyContextValue {
  const ctx = useContext(FilterApplyContext)
  if (!ctx) {
    throw new Error('useFilterApply must be used within a DataTableStoreSync')
  }
  return ctx
}

export function DataTableStoreSync({
  children,
}: {
  children?: React.ReactNode
}) {
  const value = useDataTableStoreSync()
  return (
    <FilterApplyContext.Provider value={value}>
      {children}
    </FilterApplyContext.Provider>
  )
}

/**
 * Hook version for more control - exposes filter apply + dirty state, and syncs
 * sorting and selection immediately.
 */
export function useDataTableStoreSync(): FilterApplyContextValue {
  const context = useStoreContext()
  const { table, filterFields, sorting, rowSelection } = useDataTable()
  const { setFilters } = useFilterActions()

  const lastSentSortRef = useRef<{ id: string; desc: boolean } | null>(null)
  const lastSentUuidRef = useRef<string>('')
  const isInitialMount = useRef(true)

  const columnFilters = table.getState().columnFilters

  const filterFieldKeys = useMemo(
    () => new Set(filterFields?.map((f) => f.value as string) ?? []),
    [filterFields]
  )

  // The live draft: the managed filter values currently held in the table.
  const currentFilters = useMemo(() => {
    const filters: Record<string, unknown> = {}
    for (const filter of columnFilters) {
      if (filterFieldKeys.has(filter.id)) {
        filters[filter.id] = filter.value
      }
    }
    return filters
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnFilters identity
  }, [columnFilters, filterFieldKeys])

  // The filters last pushed to BYOS (URL / query) — i.e. what the results reflect.
  const [appliedFilters, setAppliedFilters] = useState<Record<string, unknown>>(
    {}
  )

  // Initial mount: seed table from BYOS, establish the applied baseline, and push
  // it so the first query reflects any default/URL filters (BYOS ↔ Table).
  useEffect(() => {
    if (!context) return
    if (!isInitialMount.current) return
    isInitialMount.current = false

    const seeded: Record<string, unknown> = { ...currentFilters }
    const byosState = context.adapter.getSnapshot().state as Record<
      string,
      unknown
    >
    for (const key of filterFieldKeys) {
      if (key in seeded) continue
      const byosValue = byosState[key]
      if (byosValue == null) continue
      if (Array.isArray(byosValue) && byosValue.length === 0) continue
      table.getColumn(key)?.setFilterValue(byosValue)
      seeded[key] = byosValue
    }

    setAppliedFilters(seeded)
    if (Object.keys(seeded).length > 0) {
      setFilters(seeded)
    }

    lastSentSortRef.current = sorting?.[0] || null
    const selectedKeys = Object.keys(rowSelection || {})
    lastSentUuidRef.current =
      selectedKeys.length > 0 ? (selectedKeys[0] ?? '') : ''
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [context])

  const applyFilters = useCallback(() => {
    if (!context) return
    const updates: Record<string, unknown> = { ...currentFilters }
    // Null out keys that were applied before but have since been cleared.
    for (const key of Object.keys(appliedFilters)) {
      if (!(key in currentFilters)) {
        updates[key] = null
      }
    }
    setAppliedFilters({ ...currentFilters })
    setFilters(updates)
  }, [context, currentFilters, appliedFilters, setFilters])

  const isDirty = useMemo(
    () => !isStateEqual(currentFilters, appliedFilters),
    [currentFilters, appliedFilters]
  )

  // Sync sorting (immediate — not part of the filter panel)
  useEffect(() => {
    if (!context) return
    if (isInitialMount.current) return

    const newSort = sorting?.[0] || null
    const lastSort = lastSentSortRef.current

    if (newSort?.id === lastSort?.id && newSort?.desc === lastSort?.desc) {
      return
    }

    lastSentSortRef.current = newSort
    setFilters({ sort: newSort })
  }, [sorting, context, setFilters])

  // Sync row selection (immediate)
  useEffect(() => {
    if (!context) return
    if (isInitialMount.current) return

    const selectedKeys = Object.keys(rowSelection || {})
    const newUuid = selectedKeys.length > 0 ? selectedKeys[0] : null
    const newUuidStr = newUuid || ''

    if (newUuidStr === lastSentUuidRef.current) {
      return
    }

    lastSentUuidRef.current = newUuidStr
    setFilters({ uuid: newUuid })
  }, [rowSelection, context, setFilters])

  return useMemo(() => ({ applyFilters, isDirty }), [applyFilters, isDirty])
}
